import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import {
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoTableAction,
  PoModalModule,
  PoModalComponent,
  PoModalAction,
  PoSelectOption
} from '@po-ui/ng-components';

import { Opcao, RecursoRegra, RegraBase } from '../shared/regras-api.service';
import { RegrasListaBase } from '../shared/regras-lista.base';
import { RegrasSkeletonComponent, RegrasToolbarComponent } from '../shared/regras-toolbar';
import { PoComboListaVisivelDirective } from '../../../shared/po-combo-lista-visivel.directive';

/** Regra de TES do CT-e (tabela ZOI). */
export interface RegraCte extends RegraBase {
  codigo?: string;
  filial: string;
  tipoProcesso: string;
  geraFinanceiro: string;
  icms: string;
  simples: string;
  tes: string;
  natureza: string;
  descricao: string;
  ordem: string;
  dtIni: string;
  dtFim: string;
  vigencia?: string;
}

/**
 * Aba de regras de classificação — CT-e (ZOI).
 *
 * O formulário é **reactive form**, e não `ngModel`: é o que o `po-combo` com
 * `p-filter-service` exige. Com `[ngModel]` de mão única a lista carregada pelo
 * serviço não chegava a ser exibida, e `[(ngModel)]` nem compila — o componente
 * declara um output `ngModelChange` próprio, que colide com o do `NgModel`
 * (NG8007). O exemplo oficial do PO-UI usa `formControlName` pelo mesmo motivo.
 *
 * Duas particularidades do domínio que a tela precisa deixar visíveis:
 *
 * 1. "Simples Nacional" é o que realmente decide a regra. No código antigo a
 *    coluna se chamava ICMS mas era governada pelo Simples — a tela separa os
 *    dois campos para acabar com a ambiguidade.
 *
 * 2. ICMS "Indiferente" é o valor normal, não a exceção. O portal devolve todas
 *    as TES candidatas e só prefere a regra de ICMS isento quando o XML traz
 *    CST 40/41. Marcar uma regra como "Tributado" a tira do caminho da nota
 *    isenta — por isso o campo abre em Indiferente.
 */
@Component({
  selector: 'app-regras-cte',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoModalModule,
    RegrasToolbarComponent,
    RegrasSkeletonComponent,
    PoComboListaVisivelDirective
  ],
  templateUrl: './cte.html',
  styleUrls: ['./cte.css']
})
export class RegrasCteComponent extends RegrasListaBase<RegraCte> {
  @ViewChild('formModal', { static: true }) formModal!: PoModalComponent;

  protected readonly recurso: RecursoRegra = 'cte';
  protected readonly rotulo = 'as regras de CT-e';

  private readonly fb = inject(FormBuilder);

  editing: RegraCte | null = null;
  duplicando = false;

  readonly formGroup: FormGroup = this.fb.group({
    codigo: [''],
    filial: ['', Validators.required],
    tipoProcesso: ['', Validators.required],
    geraFinanceiro: ['', Validators.required],
    // Indiferente é o default correto: ver o comentário da classe.
    icms: ['3', Validators.required],
    simples: ['', Validators.required],
    tes: ['', Validators.required],
    natureza: ['293014', Validators.required],
    descricao: [''],
    ordem: ['', Validators.required],
    dtIni: [this.hojeIso()],
    dtFim: [''],
    ativo: ['1', Validators.required]
  });

  filiaisOptions: PoSelectOption[] = [];

  readonly tipoProcessoOptions: PoSelectOption[] = [
    { label: 'Conta e Ordem', value: '1' },
    { label: 'Encomenda/Própria', value: '2' },
    { label: 'Sem Processo', value: '3' }
  ];

  readonly icmsOptions: PoSelectOption[] = [
    { label: 'Indiferente', value: '3' },
    { label: 'Isento', value: '2' },
    { label: 'Tributado', value: '1' }
  ];

  /** Filial em edição — alimenta o `p-filter-params` do combo de TES. */
  get filialSelecionada(): string {
    return this.formGroup.get('filial')?.value ?? '';
  }

  private _filtroTes: { filial: string } = { filial: '' };

  /**
   * Parâmetros do combo de TES, com referência ESTÁVEL.
   *
   * Um literal no template (`[p-filter-params]="{ filial: ... }"`) cria objeto
   * novo a cada ciclo de detecção; o po-combo entende que o filtro mudou e
   * refaz a busca em looping. Só troca o objeto quando a filial muda de fato.
   */
  get filtroTes(): { filial: string } {
    const filial = this.filialSelecionada;
    if (this._filtroTes.filial !== filial) {
      this._filtroTes = { filial };
    }
    return this._filtroTes;
  }

  get modalTitle(): string {
    if (this.duplicando) { return 'Duplicar regra de CT-e'; }
    return this.editing ? 'Editar regra de CT-e' : 'Nova regra de CT-e';
  }

  readonly columns: Array<PoTableColumn> = [
    {
      property: 'codigo', label: 'Código', width: '110px', type: 'link',
      action: (_: string, row: RegraCte) => this.editar(row)
    },
    { property: 'filial', label: 'Filial', width: '130px' },
    {
      property: 'tipoProcesso', label: 'Processo', width: '200px', type: 'label',
      labels: [
        { value: '1', label: 'Conta e Ordem', color: 'color-08' },
        { value: '2', label: 'Encomenda/Própria', color: 'color-01' },
        { value: '3', label: 'Sem Processo', color: 'color-11' }
      ]
    },
    {
      property: 'geraFinanceiro', label: 'Financeiro', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Sim', color: 'color-10' },
        { value: '2', label: 'Não', color: 'color-07' }
      ]
    },
    {
      property: 'icms', label: 'ICMS', width: '140px', type: 'label',
      labels: [
        { value: '1', label: 'Tributado', color: 'color-01' },
        { value: '2', label: 'Isento', color: 'color-11' },
        { value: '3', label: 'Indiferente', color: 'color-09' }
      ]
    },
    {
      property: 'simples', label: 'Simples', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Sim', color: 'color-10' },
        { value: '2', label: 'Não', color: 'color-07' }
      ]
    },
    { property: 'tes', label: 'TES', width: '100px' },
    { property: 'natureza', label: 'Natureza', width: '130px' },
    { property: 'ordem', label: 'Ordem', width: '110px' },
    { property: 'vigencia', label: 'Vigência', width: '180px' },
    {
      property: 'ativo', label: 'Situação', width: '130px', type: 'label',
      labels: [
        { value: '1', label: 'Ativa', color: 'color-10' },
        { value: '2', label: 'Inativa', color: 'color-07' }
      ]
    },
    { property: 'descricao', label: 'Descrição' }
  ];

  readonly tableActions: Array<PoTableAction> = [
    { label: 'Editar', icon: 'an an-pencil-simple', action: (row: RegraCte) => this.editar(row) },
    { label: 'Duplicar', icon: 'an an-copy', action: (row: RegraCte) => this.duplicar(row) },
    {
      label: 'Inativar', icon: 'an an-lock', type: 'danger',
      action: (row: RegraCte) => this.alterarSituacao(row, '2'),
      visible: (row: RegraCte) => row.ativo === '1'
    },
    {
      label: 'Reativar', icon: 'an an-lock-open',
      action: (row: RegraCte) => this.alterarSituacao(row, '1'),
      visible: (row: RegraCte) => row.ativo === '2'
    }
  ];

  readonly modalPrimaryAction: PoModalAction = { action: () => this.salvar(), label: 'Salvar' };
  readonly modalSecondaryAction: PoModalAction = { action: () => this.formModal.close(), label: 'Cancelar' };

  protected override aoIniciar(): void {
    this.api.filiais().subscribe({
      next: (o: Opcao[]) => this.filiaisOptions = o,
      error: () => this.notif.warning('Não foi possível carregar a lista de filiais.')
    });

    // A TES tem de existir na filial DA REGRA, não na filial logada: trocar a
    // filial invalida a TES já escolhida.
    //
    // O writeValue do po-combo, ao receber valor vazio com serviço ativo, chama
    // updateComboList() e ESVAZIA a lista aberta. Por isso só limpa quando havia
    // TES escolhida — escrever vazio por escrever derrubaria a lista.
    this.formGroup.get('filial')?.valueChanges.subscribe(() => {
      const tes = this.formGroup.get('tes');
      if (tes?.value) {
        tes.setValue('', { emitEvent: false });
      }
    });

    this.formGroup.get('natureza')?.valueChanges.subscribe((valor: string) => {
      this.preencherDescricao(valor);
    });
  }

  protected override decorar(regra: RegraCte): RegraCte {
    return { ...regra, vigencia: this.vigenciaDe(regra.dtIni, regra.dtFim) };
  }

  protected corpo(regra: RegraCte): Record<string, string> {
    // Código e auditoria ficam de fora: o backend os carimba pelo modelo MVC.
    const body: Record<string, string> = {
      filial: regra.filial,
      tipoProcesso: regra.tipoProcesso,
      geraFinanceiro: regra.geraFinanceiro,
      icms: regra.icms,
      simples: regra.simples,
      tes: regra.tes,
      natureza: regra.natureza,
      descricao: regra.descricao ?? '',
      ordem: regra.ordem,
      dtIni: regra.dtIni ?? '',
      dtFim: regra.dtFim ?? '',
      ativo: regra.ativo
    };
    if (regra.codigo) { body['codigo'] = regra.codigo; }
    return body;
  }

  abrirNova(): void {
    this.editing = null;
    this.duplicando = false;
    this.formGroup.reset(this.valoresPadrao());
    this.formModal.open();
  }

  editar(regra: RegraCte): void {
    this.editing = regra;
    this.duplicando = false;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra });
    this.formModal.open();
  }

  /**
   * Duplicar abre o formulário com os valores da origem e SEM código, para o
   * backend tratar como inclusão. A ordem também é limpa: mantê-la criaria duas
   * regras ativas competindo pela mesma nota com a mesma ordem, que é
   * exatamente o que o modelo recusa na gravação.
   */
  duplicar(regra: RegraCte): void {
    this.editing = null;
    this.duplicando = true;
    this.formGroup.reset({ ...this.valoresPadrao(), ...regra, codigo: '', ordem: '' });
    this.formModal.open();
  }

  salvar(): void {
    const regra = this.formGroup.getRawValue() as RegraCte;
    regra.ordem = this.normalizarOrdem(regra.ordem);

    const erro = this.validar(regra);
    if (erro) {
      this.notif.warning(erro);
      return;
    }

    this.gravar(regra, !!this.editing?.codigo, () => this.formModal.close());
  }

  private validar(f: RegraCte): string {
    if (!f.filial) { return 'Informe a filial a que a regra se aplica.'; }
    if (!f.tipoProcesso) { return 'Informe o tipo de processo.'; }
    if (!f.geraFinanceiro) { return 'Informe se a regra gera financeiro.'; }
    if (!f.icms) { return 'Informe a situação do ICMS.'; }
    if (!f.simples) { return 'Informe se o emitente é do Simples Nacional.'; }
    if (!f.tes) { return 'Informe o tipo de operação (TES).'; }
    if (!f.natureza) { return 'Informe a natureza financeira.'; }
    const ordem = String(f.ordem ?? '').trim();
    if (!ordem || !/^\d{1,3}$/.test(ordem) || Number(ordem) < 1) {
      return 'Informe a ordem de avaliação (1 a 999).';
    }
    if (f.dtIni && f.dtFim && f.dtFim < f.dtIni) {
      return 'O fim da vigência não pode ser anterior ao início.';
    }
    return '';
  }

  /** Preenche a descrição com o texto da natureza escolhida. */
  private preencherDescricao(natureza: string): void {
    if (!natureza) { return; }
    this.api.naturezas(natureza).subscribe({
      next: itens => {
        const achado = itens.find(i => i.value === natureza);
        if (!achado) { return; }
        // O rótulo vem como "253011 - DESCRICAO": guarda só a descrição.
        const partes = achado.label.split(' - ');
        const texto = partes.length > 1 ? partes.slice(1).join(' - ') : achado.label;
        this.formGroup.get('descricao')?.setValue(texto.substring(0, 100), { emitEvent: false });
      },
      error: () => { /* descrição fica em branco; o usuário pode digitar */ }
    });
  }

  private valoresPadrao(): Partial<RegraCte> {
    return {
      codigo: '', filial: '', tipoProcesso: '', geraFinanceiro: '',
      icms: '3', simples: '', tes: '', natureza: '293014', descricao: '',
      ordem: '', dtIni: this.hojeIso(), dtFim: '', ativo: '1'
    };
  }
}
